import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BsClockFill,
  BsGeoAlt,
  BsTelephone,
  BsEnvelope,
  BsCheckCircle,
  BsXCircle,
} from "react-icons/bs";
import Button from "../components/ui/Button";

const contactInfo = [
  {
    icon: BsGeoAlt,
    title: "Visit Us",
    content: "GE-238-1051",
    link: "https://goo.gl/maps/1234567890",
  },
  {
    icon: BsTelephone,
    title: "Call Us",
    content: "+233 24 609 9153",
    link: "tel:+233246099153",
  },
  {
    icon: BsEnvelope,
    title: "Email Us",
    content: "acquwaanaturals@gmail.com",
    link: "mailto:acquwaanaturals@gmail.com",
  },
  {
    icon: BsClockFill,
    title: "Opening Hours",
    content: "Mon-Sat: 9AM - 6PM",
  },
];

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className={`fixed bottom-4 right-4 flex items-center gap-2 px-6 py-3 rounded-lg shadow-lg ${
        type === "success" ? "bg-[#DB5461] text-white" : "bg-red-500 text-white"
      }`}
    >
      {type === "success" ? (
        <BsCheckCircle className="w-5 h-5" />
      ) : (
        <BsXCircle className="w-5 h-5" />
      )}
      <p className="font-medium">{message}</p>
      <button
        onClick={onClose}
        className="ml-4 hover:opacity-80 transition-opacity"
      >
        <BsXCircle className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch("https://formspree.io/f/mblgdoal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // Reset form
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
        // Show success toast
        setToast({
          type: "success",
          message: "Message sent successfully! We'll get back to you soon.",
        });
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      // Show error toast
      setToast({
        type: "error",
        message: "Failed to send message. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const closeToast = () => setToast(null);

  return (
    <div className="bg-gray-50 pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div>
              <h1 className="text-4xl font-serif font-bold text-primary-900 mb-4">
                Get in Touch
              </h1>
              <p className="text-lg text-gray-600">
                Have questions about our products or services? We're here to
                help!
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-[#DB5461]/10 rounded-lg">
                    <BsGeoAlt className="h-6 w-6 text-[#DB5461]" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">
                    Our Location
                  </h3>
                  <p className="mt-1 text-gray-600">GE-238-1051</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-[#DB5461]/10 rounded-lg">
                    <BsTelephone className="h-6 w-6 text-[#DB5461]" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">
                    Phone Number
                  </h3>
                  <p className="mt-1 text-gray-600">+233 24 609 9153</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-[#DB5461]/10 rounded-lg">
                    <BsEnvelope className="h-6 w-6 text-[#DB5461]" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">
                    Email
                  </h3>
                  <p className="mt-1 text-gray-600">
                    acquwaanaturals@gmail.com
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-[#DB5461]/10 rounded-lg">
                    <BsClockFill className="h-6 w-6 text-[#DB5461]" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">
                    Business Hours
                  </h3>
                  <p className="mt-1 text-gray-600">
                    Monday - Friday: 9am - 6pm
                    <br />
                    Saturday: 10am - 4pm
                    <br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-soft p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jane Smith"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="janesmith@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Product Inquiry"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="I would like to know more about..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#DB5461] hover:bg-[#DB5461]/90"
                loading={loading}
              >
                Send Message
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
      <AnimatePresence>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={closeToast}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
